/**
 * Reloj basado en marcas de tiempo, no en contar tics.
 *
 * Un `setInterval` que resta un segundo se desincroniza en cuanto el móvil
 * ralentiza la pestaña. Guardando el instante de arranque y el tiempo ya
 * acumulado, el tiempo transcurrido siempre es correcto aunque el navegador
 * se salte fotogramas o la app pase a segundo plano.
 */

export type ClockStatus = 'idle' | 'running' | 'paused'

export interface ClockState {
  readonly status: ClockStatus
  /** Milisegundos acumulados en tramos ya cerrados. */
  readonly accumulatedMs: number
  /** Instante (performance-independiente) del arranque del tramo en curso. */
  readonly startedAt: number | null
}

export const IDLE_CLOCK: ClockState = { status: 'idle', accumulatedMs: 0, startedAt: null }

export function startClock(clock: ClockState, now: number): ClockState {
  if (clock.status === 'running') return clock
  return { status: 'running', accumulatedMs: clock.accumulatedMs, startedAt: now }
}

export function pauseClock(clock: ClockState, now: number): ClockState {
  if (clock.status !== 'running' || clock.startedAt === null) return clock
  return {
    status: 'paused',
    accumulatedMs: clock.accumulatedMs + (now - clock.startedAt),
    startedAt: null,
  }
}

export function resetClock(): ClockState {
  return IDLE_CLOCK
}

/** Milisegundos transcurridos, corran o no las agujas. */
export function elapsedMs(clock: ClockState, now: number): number {
  if (clock.status === 'running' && clock.startedAt !== null) {
    return clock.accumulatedMs + (now - clock.startedAt)
  }
  return clock.accumulatedMs
}

/** Deja el reloj parado en un valor exacto (al cerrar una fase, por ejemplo). */
export function freezeClock(clock: ClockState, now: number): ClockState {
  return { status: 'paused', accumulatedMs: elapsedMs(clock, now), startedAt: null }
}
